import React, { useState, useEffect } from 'react'
import { Box, Popover, TextField, Button, Stack } from '@mui/material'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import type { Editor } from '@tiptap/react'

interface LinkPopoverProps {
  editor: Editor
  anchorEl: HTMLElement | null
  onClose: () => void
}

export const LinkPopover: React.FC<LinkPopoverProps> = ({ editor, anchorEl, onClose }) => {
  const { t } = useTranslation()
  const [href, setHref] = useState('')

  const isLink = editor?.isActive('link')
  const currentHref = editor?.getAttributes('link')?.href ?? ''

  useEffect(() => {
    if (anchorEl) {
      setHref(currentHref)
    }
  }, [anchorEl, currentHref])

  const handleApply = () => {
    if (!editor) return
    if (href.trim()) {
      editor.chain().focus().setLink({ href: href.trim() }).run()
    } else {
      editor.chain().focus().unsetLink().run()
    }
    onClose()
  }

  const handleRemove = () => {
    editor?.chain().focus().unsetLink().run()
    onClose()
  }

  const open = Boolean(anchorEl)

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            bgcolor: INTERAX_COLORS.heritageBlue,
            border: `1px solid ${INTERAX_COLORS.thunderGrey}`,
            borderRadius: 1,
            p: 2
          }
        }
      }}
    >
      <Box sx={{ minWidth: 280 }}>
        <TextField
          size="small"
          fullWidth
          label={t('facilitator.trainingSprint.emailEditor.insertLink')}
          placeholder="https://"
          value={href}
          onChange={(e) => setHref(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleApply()}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              bgcolor: INTERAX_COLORS.offBlack,
              color: INTERAX_COLORS.typographyText
            },
            '& .MuiInputLabel-root': { color: INTERAX_COLORS.thunderGrey }
          }}
        />
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {isLink && (
            <Button size="small" onClick={handleRemove} sx={{ color: INTERAX_COLORS.sun }}>
              {t('facilitator.trainingSprint.emailEditor.removeLink')}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{
              bgcolor: INTERAX_COLORS.sun,
              color: INTERAX_COLORS.offBlack,
              '&:hover': { bgcolor: INTERAX_COLORS.sun40 }
            }}
          >
            {t('common.save')}
          </Button>
        </Stack>
      </Box>
    </Popover>
  )
}
